import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Content is capped at this width; the remainder of the row is the "rest" region. */
export const CONTENT_MAX_WIDTH = 1550;
/** Gutter between the menu, the content and anything placed beside it. */
export const SHELL_GUTTER = 24;
export const SHELL_GUTTER_NARROW = 16;
/** Below this the narrow gutter applies (the md frames in the design use 16px). */
export const NARROW_GUTTER_MAX_WIDTH = 1450;

interface ShellMetrics {
  /** Attach to the capped content element. */
  contentRef: RefObject<HTMLDivElement>;
  /** Space to the right of the content, after the gutter — 0 when there is none. */
  restWidth: number;
  gutter: number;
  contentWidth: number;
}

/**
 * Measures the shell so right-side panels can place themselves in CSS.
 *
 * Publishes `--app-rest-width` and `--app-gutter` on the document root. Panels are positioned with
 * `right: max(0px, var(--app-rest-width) - panelWidth)`, which puts a panel beside the content when
 * the room exists and pins it to the viewport edge (overlapping) when it does not — one expression
 * instead of two layout modes.
 *
 * This has to be observed rather than read once: the menu animates between 80px and 270px, so the
 * content's right edge moves over ~200ms on every collapse or expand, and an open panel must track it.
 */
export const useShellMetrics = (): ShellMetrics => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [restWidth, setRestWidth] = useState(0);
  const [gutter, setGutter] = useState(SHELL_GUTTER);
  const [contentWidth, setContentWidth] = useState(0);

  const measure = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const viewport = document.documentElement.clientWidth;
    const nextGutter = viewport <= NARROW_GUTTER_MAX_WIDTH ? SHELL_GUTTER_NARROW : SHELL_GUTTER;
    const box = el.getBoundingClientRect();
    // The right gutter is the element's own padding (so the scrollbar renders in the gutter rather
    // than over the content), which means box.right is already the outer edge of the gutter and
    // box.width already contains it — both have to be discounted here.
    const paddingRight = parseFloat(getComputedStyle(el).paddingRight) || 0;
    const contentRight = box.right - paddingRight;
    const rest = Math.max(0, viewport - contentRight - nextGutter);

    setGutter(nextGutter);
    setRestWidth(rest);
    setContentWidth(box.width - paddingRight);
    document.documentElement.style.setProperty('--app-rest-width', `${rest}px`);
    document.documentElement.style.setProperty('--app-gutter', `${nextGutter}px`);
    // Lets overlays that belong to the content — toasts especially — anchor and size to it rather
    // than to the viewport.
    document.documentElement.style.setProperty('--app-content-width', `${Math.round(box.width - paddingRight)}px`);
    document.documentElement.style.setProperty('--app-content-right', `${Math.round(contentRight)}px`);
    document.documentElement.style.setProperty('--app-content-left', `${Math.round(box.left)}px`);
  }, []);

  // Written synchronously before paint so panels never flash at the viewport edge on first render.
  useLayoutEffect(measure);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // The content is flex-1 up to a 1550px cap, so once it is capped a menu collapse/expand moves
    // its x-position without changing its width — and ResizeObserver only reports size. Its parent
    // <main> is what actually resizes (viewport minus the menu), and it resizes on every frame of
    // the menu's 200ms transition, which keeps an open panel in step with it.
    if (el.parentElement) observer.observe(el.parentElement);
    observer.observe(document.documentElement);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  return { contentRef, restWidth, gutter, contentWidth };
};

export default useShellMetrics;
